/**
 * `rel="sponsored"` on the outbound links of paid content — Google's rule for
 * links that are part of a paid placement (sponsored and partner posts,
 * lib/lecturehub/sponsor.ts). Editorial posts are never touched.
 *
 * Runs on SANITIZED HTML (components/ui/RichHtml.tsx): the sanitizer keeps an
 * anchor's `href`, `title` and `target` and drops any `rel` an author wrote,
 * so the attribute is added, never merged. Outbound means an absolute http(s)
 * link to a host other than the portal's own; a relative link, an in-page
 * anchor or `mailto:` stays as it is. `noopener` rides along — a sponsor's
 * page has no business with `window.opener`.
 */
export const SPONSORED_REL = "sponsored noopener";

/** Whether `href` leaves the portal (`ownHost` without `www.` either side). */
export function isOutbound(href: string, ownHost: string | null): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  const bare = (h: string) => h.toLowerCase().replace(/^www\./, "");
  return !ownHost || bare(url.hostname) !== bare(ownHost);
}

export function markSponsoredLinks(html: string, ownHost: string | null): string {
  return html.replace(/<a\b([^>]*)>/gi, (tag, attrs: string) => {
    const href = attrs.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)')/i);
    const value = href?.[2] ?? href?.[3];
    if (!value || !isOutbound(value.replace(/&amp;/g, "&"), ownHost)) return tag;
    const rest = attrs.replace(/\s+rel\s*=\s*("[^"]*"|'[^']*')/gi, "");
    return `<a${rest} rel="${SPONSORED_REL}">`;
  });
}
